/* =============================================================================
 * NO PENALTY 商品ページカスタム JS
 *
 * 機能:
 *   ① 色プルダウン（自前実装、大きな swatch + 日本語名、Dawn の DOM 置換にも対応）
 *   ② 画像即時切替
 *   ③ サイズ表モーダル + 商品説明欄への動的サイズ表挿入
 *   ④ 配送日アイコン（数量フォーム右、簡略表示）
 *   ⑤ 商品タイトルを画像の上に移動
 *   ⑥ Sticky add-to-cart bar（PC・SP）
 * ============================================================================= */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // 1. データ取得
  // -------------------------------------------------------------------------
  function loadProductData() {
    const el = document.getElementById('ns-product-data');
    if (!el) return null;
    try {
      const json = JSON.parse(el.textContent || '{}');
      const meta = json.metafields || {};
      for (const k of ['color_swatches', 'variant_fulfillment', 'size_chart', 'size_intro']) {
        if (typeof meta[k] === 'string' && meta[k]) {
          try { meta[k] = JSON.parse(meta[k]); } catch (_) { meta[k] = null; }
        }
      }
      return json;
    } catch (e) {
      console.warn('[ns-product] data parse failed', e);
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // 2. 採寸ラベル英→日辞書
  //    Printful の "measure_yourself" 表は身体に巻いて測る前提のため、
  //    Chest = 「胸囲」(身体一周) として表示する方が直感的。
  // -------------------------------------------------------------------------
  const MEASURE_LABEL_JP = {
    'Length': '着丈', 'Length (HSP)': '着丈', 'Body Length': '着丈',
    'Chest': '胸囲', 'Chest Width': '胸囲', 'Bust': 'バスト', 'Width': '胸囲',
    'Waist': 'ウエスト', 'Hips': 'ヒップ', 'Hip': 'ヒップ',
    'Sleeve length': '袖丈', 'Sleeve Length': '袖丈', 'Sleeve': '袖丈',
    'Sleeve length (from center back)': '袖丈',
    'Shoulder': '肩幅', 'Shoulder width': '肩幅', 'Shoulder Width': '肩幅',
    'Inseam': '股下', 'Outseam': '総丈', 'Rise': '股上',
    'Front rise': '前股上', 'Back rise': '後股上',
    'Thigh': 'ワタリ', 'Knee': '膝幅',
    'Leg opening': '裾幅', 'Leg Opening': '裾幅',
    'Head circumference': '頭周り', 'Hat depth': '帽子の深さ',
    'Brim': 'つば幅', 'Cuff': '袖口',
  };
  const translateMeasureLabel = (en) => MEASURE_LABEL_JP[en] || en || '';

  function isLightColor(hex) {
    if (!hex) return false;
    const m = hex.replace('#', '').match(/^([\da-f]{6})$/i);
    if (!m) return false;
    const r = parseInt(m[1].slice(0, 2), 16);
    const g = parseInt(m[1].slice(2, 4), 16);
    const b = parseInt(m[1].slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 220;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // -------------------------------------------------------------------------
  // 3. 色プルダウン（自前実装）
  //    - 元の fieldset の radio は残す（Dawn の DOM 連携を壊さない）
  //    - fieldset を CSS で hide → 代わりに自前 dropdown UI を表示
  //    - Dawn のバージョンによって構造が異なる：
  //      旧: <div.product-form__input><fieldset>...</fieldset></div>
  //      新: <fieldset.product-form__input>...</fieldset>
  //    - どちらでも picker を fieldset の直前（同じ親内）に挿入する。
  // -------------------------------------------------------------------------
  function findColorFieldsets() {
    const result = [];
    document.querySelectorAll('.product-form__input').forEach(fs => {
      const legend = fs.querySelector('legend, label.form__label');
      const labelText = (legend?.textContent || '').trim();
      if (!/色|color|colour/i.test(labelText)) return;

      const innerFieldset = fs.querySelector('fieldset');
      let visibleEl, container, refNode;
      if (innerFieldset) {
        // 旧構造：fs(div) > fieldset
        visibleEl = innerFieldset;
        container = fs;
        refNode   = innerFieldset;
      } else {
        // 新構造：fs 自体が fieldset
        visibleEl = fs;
        container = fs.parentNode;
        refNode   = fs;
      }
      if (container && refNode) {
        result.push({ fs, visibleEl, container, refNode });
      }
    });
    return result;
  }

  function applyColorPicker(data) {
    const swatches = data.metafields?.color_swatches?.swatches || [];
    const fieldsets = findColorFieldsets();

    // データが無いとき or 色 fieldset が無いときはフォールバック：原本を必ず可視化
    if (swatches.length === 0) {
      document.querySelectorAll('.ns-color-fieldset-original')
        .forEach(el => el.classList.remove('ns-color-fieldset-original'));
      document.querySelectorAll('.ns-color-picker').forEach(p => p.remove());
      return;
    }

    const swatchByEn = new Map(swatches.map(s => [s.name_en, s]));

    fieldsets.forEach(({ fs, visibleEl, container, refNode }) => {
      const checkedRadio = fs.querySelector('input[type="radio"]:checked');
      const currentValue = checkedRadio?.value || '';

      // 既に dropdown を作成済みかチェック
      let picker = container.querySelector(':scope > .ns-color-picker');

      // 既にあって、選択中の値が同じならスキップ
      if (picker && picker.dataset.currentValue === currentValue) {
        visibleEl.classList.add('ns-color-fieldset-original');
        return;
      }

      if (picker) picker.remove();
      picker = buildColorPicker(fs, swatchByEn);
      if (!picker) {
        // 構築失敗時は安全側に倒して原本を可視化
        visibleEl.classList.remove('ns-color-fieldset-original');
        return;
      }
      // picker 生成に成功してから初めて原本を隠す
      visibleEl.classList.add('ns-color-fieldset-original');
      try {
        container.insertBefore(picker, refNode);
      } catch (_) {
        // 何かあれば原本を戻す
        visibleEl.classList.remove('ns-color-fieldset-original');
      }
    });
  }

  function buildColorPicker(fieldset, swatchByEn) {
    const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
    if (radios.length === 0) return null;

    const checked = radios.find(r => r.checked) || radios[0];
    const checkedEn = checked.value;
    const checkedSw = swatchByEn.get(checkedEn);
    const checkedJp = checkedSw?.name_jp || checkedEn;
    const checkedHex = checkedSw?.hex || '#ccc';

    const container = document.createElement('div');
    container.className = 'ns-color-picker';
    container.dataset.currentValue = checkedEn;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'ns-color-picker__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = `
      <span class="ns-color-circle ns-color-circle--lg" style="background: ${escapeHtml(checkedHex)};"${isLightColor(checkedHex) ? ' data-light="true"' : ''}></span>
      <span class="ns-color-picker__label">${escapeHtml(checkedJp)}</span>
      <span class="ns-color-picker__caret" aria-hidden="true">
        <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
    `;
    container.appendChild(trigger);

    const list = document.createElement('ul');
    list.className = 'ns-color-picker__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    radios.forEach(r => {
      const en = r.value;
      const sw = swatchByEn.get(en);
      const jp = sw?.name_jp || en;
      const hex = sw?.hex || '#ccc';
      const isChecked = r.checked;
      const isDisabled = r.disabled || r.classList.contains('disabled');

      const li = document.createElement('li');
      li.className = 'ns-color-picker__item';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', isChecked ? 'true' : 'false');
      li.dataset.value = en;
      if (isDisabled) li.dataset.disabled = 'true';
      li.innerHTML = `
        <span class="ns-color-circle ns-color-circle--lg" style="background: ${escapeHtml(hex)};"${isLightColor(hex) ? ' data-light="true"' : ''}></span>
        <span class="ns-color-picker__item-label">${escapeHtml(jp)}</span>
        ${isChecked ? '<span class="ns-color-picker__check" aria-hidden="true">✓</span>' : ''}
      `;
      list.appendChild(li);
    });
    container.appendChild(list);

    // open/close
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      list.hidden = expanded;
    });
    document.addEventListener('click', e => {
      if (!container.contains(e.target)) {
        trigger.setAttribute('aria-expanded', 'false');
        list.hidden = true;
      }
    });

    // 選択
    list.addEventListener('click', e => {
      const item = e.target.closest('.ns-color-picker__item');
      if (!item || item.dataset.disabled === 'true') return;
      const val = item.dataset.value;
      const radio = radios.find(r => r.value === val);
      if (!radio) return;
      // Dawn が radio change で variant 切替を行う
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.click();
      trigger.setAttribute('aria-expanded', 'false');
      list.hidden = true;
    });

    // キーボード
    trigger.addEventListener('keydown', e => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        trigger.setAttribute('aria-expanded', 'true');
        list.hidden = false;
        list.querySelector('.ns-color-picker__item')?.focus();
      }
    });

    return container;
  }

  // -------------------------------------------------------------------------
  // 4. 画像即時切替
  // -------------------------------------------------------------------------
  function setupInstantImageSwap(data) {
    const variants = data.variants || [];

    function tryHandle(input) {
      if (!input?.matches?.('input[type="radio"]')) return;
      const fs = input.closest('.product-form__input, fieldset');
      const legend = fs?.querySelector('legend, label.form__label');
      if (!legend || !/色|color|colour/i.test(legend.textContent.trim())) return;

      const colorVal = input.value;
      const matchVariant = variants.find(v => (v.options || []).some(o => o === colorVal));
      const featured = matchVariant?.featured_image;
      if (!featured?.src) return;
      swapMainImage(featured);
    }

    document.addEventListener('change', e => tryHandle(e.target), true);
    // 自前 picker のクリックでも反応
    document.addEventListener('click', e => {
      const item = e.target.closest('.ns-color-picker__item');
      if (!item) return;
      const val = item.dataset.value;
      const matchVariant = variants.find(v => (v.options || []).some(o => o === val));
      const featured = matchVariant?.featured_image;
      if (featured?.src) swapMainImage(featured);
    }, true);
  }

  // スワップ先は常に「ギャラリー先頭のメディア＝バリアント枠」に固定する。
  // 以前は .is-active を対象にしていたが、Dawn の updateMedia は §8.5 で
  // no-op 化しているため、スライド/サムネイル移動後は is-active が
  // 「見えているスライド」を指さない。その状態で色を替えると
  // (1) 見えていない写真だけ差し替わり「切り替わらない」ように見える
  // (2) 移動先の別カット写真を上書きして失う、の 2 つの不具合になっていた。
  function swapMainImage(featured) {
    if (!featured?.src) return;
    const gallery = document.querySelector('media-gallery');
    const list = gallery?.querySelector('[id^="Slider-Gallery"], .product__media-list');
    const slot =
      list?.querySelector('.product__media-item') ||
      document.querySelector('media-gallery .product__media-item, product-info .product__media-item');
    const img = slot?.querySelector('img') || document.querySelector('media-gallery img');
    if (!img) return;
    img.removeAttribute('srcset');
    img.src = featured.src;
    if (featured.alt) img.alt = featured.alt;
    if (slot) showMediaSlot(gallery, slot);
  }

  // 差し替えたバリアント枠を「いま見えている状態」へ戻す。
  // ページの縦スクロールは絶対にしない（Dawn setActiveMedia の window.scrollTo が
  // 二重演出の原因だった経緯があるため、クラス付替と横スクロール復帰のみ行う）。
  function showMediaSlot(gallery, slot) {
    const list = slot.parentElement;
    if (!list) return;
    list.querySelectorAll('.product__media-item.is-active').forEach(el => {
      if (el !== slot) el.classList.remove('is-active');
    });
    slot.classList.add('is-active');
    // SP の横スライダー（PC サムネイル型は非 active が display:none で横幅超過なし）
    if (list.scrollWidth > list.clientWidth + 1) {
      const left = slot.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
      try { list.scrollTo({ left, behavior: 'auto' }); } catch (_) { list.scrollLeft = left; }
    }
    // サムネイルの選択状態も同期（Dawn の media-gallery API がある場合のみ）
    const mediaId = slot.dataset.mediaId;
    if (gallery && mediaId && typeof gallery.setActiveThumbnail === 'function') {
      const thumb = gallery.querySelector(`[data-target="${mediaId}"]`);
      if (thumb?.querySelector('button')) {
        try { gallery.setActiveThumbnail(thumb); } catch (_) {}
      }
    }
  }

  // -------------------------------------------------------------------------
  // 5. 配送日アイコン
  // -------------------------------------------------------------------------
  function ensureDeliveryInfo(data) {
    const fulfillmentMap = data.metafields?.variant_fulfillment?.map || {};
    const jpDays = data.metafields?.delivery_japan_days || '2〜5 営業日';
    const ovDays = data.metafields?.delivery_overseas_days || '8〜14 営業日';

    const quantityWrap = findQuantityWrap();
    if (!quantityWrap) return;
    quantityWrap.classList.add('ns-quantity-grid');

    let el = quantityWrap.querySelector(':scope > .ns-delivery-info');
    if (!el) {
      el = createDeliveryInfoEl();
      quantityWrap.appendChild(el);
    }
    updateDeliveryRegion(el, data, fulfillmentMap, jpDays, ovDays);
  }

  function findQuantityWrap() {
    return (
      document.querySelector('.product-form__input--quantity') ||
      document.querySelector('.product-form__quantity') ||
      document.querySelector('.product-form__input.quantity') ||
      document.querySelector('quantity-form')?.parentElement ||
      document.querySelector('quantity-input')?.closest('.product-form__input')
    );
  }

  function createDeliveryInfoEl() {
    const wrap = document.createElement('div');
    wrap.className = 'ns-delivery-info';
    wrap.setAttribute('data-region', 'japan');
    wrap.setAttribute('aria-live', 'polite');
    wrap.setAttribute('role', 'button');
    wrap.setAttribute('tabindex', '0');
    wrap.setAttribute('aria-label', 'サイズ・カラー別のお届け目安一覧を開く');
    wrap.innerHTML = `
      <span class="ns-delivery-info__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 7h11v8H3V7zm12 3h3l3 3v2h-2a2 2 0 0 1-4 0h-1V7zm-9 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
        </svg>
      </span>
      <span class="ns-delivery-info__main">
        <span class="ns-delivery-info__days">
          <span class="ns-delivery-info__days-prefix">お届け目安</span>
          <span class="ns-delivery-info__days-num"></span>
          <span class="ns-delivery-info__days-suffix"></span>
        </span>
        <span class="ns-delivery-info__sub">
          <span class="ns-delivery-info__free">全品送料無料</span>
          <span class="ns-delivery-info__hint">タップで一覧を表示</span>
        </span>
      </span>
    `;
    return wrap;
  }

  function updateDeliveryRegion(el, data, fulfillmentMap, jpDays, ovDays) {
    if (!el) return;
    const variantId = getCurrentVariantId();
    const sku = findSkuByVariantId(data, variantId);
    const printfulVariantId = sku ? extractPrintfulVariantId(sku) : null;
    const region = (printfulVariantId && fulfillmentMap[printfulVariantId])
      || fallbackRegionByOptions(data);

    el.setAttribute('data-region', region);
    // 「発送元」を明示（POD最大の信頼シグナル「日本から発送」を数字の裏に隠さない）
    const prefixEl = el.querySelector('.ns-delivery-info__days-prefix');
    if (prefixEl) prefixEl.textContent = region === 'japan' ? '日本から発送' : '海外から発送';
    // 数字（"2〜5"）と単位（"営業日"）を分離して、数字だけ強調表示できるようにする。
    // メタフィールド値はベタ組み（"2〜5営業日"）・旧スペース入り（"2〜5 営業日"）の両対応
    const days = region === 'japan' ? jpDays : ovDays;
    const m = String(days || '').match(/^(.+?)\s*(営業日.*)$/);
    const num = m ? m[1] : (days || '');
    const suffix = m ? m[2] : '';
    const numEl = el.querySelector('.ns-delivery-info__days-num');
    const sufEl = el.querySelector('.ns-delivery-info__days-suffix');
    if (numEl) numEl.textContent = num;
    if (sufEl) sufEl.textContent = suffix;
  }

  function getCurrentVariantId() {
    const idInput = document.querySelector('.product-form__input input[name="id"], product-form input[name="id"]');
    if (idInput?.value) return idInput.value;
    const selScript = document.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
    if (selScript) {
      try { return JSON.parse(selScript.textContent)?.id; } catch (_) {}
    }
    return null;
  }

  function fallbackRegionByOptions(data) {
    const swatches = data.metafields?.color_swatches?.swatches || [];
    const allRadios = document.querySelectorAll('.product-form__input input[type="radio"]:checked');
    let size = null;
    for (const r of allRadios) {
      const fs = r.closest('.product-form__input, fieldset');
      const legend = fs?.querySelector('legend, label.form__label');
      if (legend && /^サイズ|^size/i.test(legend.textContent.trim())) {
        size = r.value;
        break;
      }
    }
    if (size && swatches.length > 0) {
      for (const s of swatches) {
        if ((s.japan_sizes || []).includes(size)) return 'japan';
      }
    }
    return 'overseas';
  }

  function extractPrintfulVariantId(sku) {
    if (!sku) return null;
    const m = sku.match(/_(\d+)$/);
    return m ? m[1] : null;
  }
  function findSkuByVariantId(data, variantId) {
    if (!variantId) return null;
    const id = String(variantId).split('/').pop();
    const v = (data.variants || []).find(x => String(x.id) === id);
    return v?.sku || null;
  }

  // -------------------------------------------------------------------------
  // 6. サイズ表（モーダル + 商品説明欄に動的挿入）
  //    Dawn が variant 切替時に variant-selects の innerHTML を置換するため、
  //    トリガーは variant-selects の「外」（兄弟要素）に置いて DOM 置換の影響を避ける。
  // -------------------------------------------------------------------------
  function setupSizeGuide(data) {
    const sizeChart = data.metafields?.size_chart;
    if (!sizeChart || !sizeChart.size_tables || sizeChart.size_tables.length === 0) return;
    ensureSizeModal(sizeChart, data);
    ensureSizeGuideAnchor(sizeChart, data);
  }

  function ensureSizeModal(sizeChart, data) {
    let modal = document.querySelector('.ns-size-modal');
    if (modal) return modal;
    modal = buildSizeModal(sizeChart, data);
    document.body.appendChild(modal);
    modal.querySelector('.ns-size-modal__close').addEventListener('click', () => closeModal(modal));
    modal.addEventListener('click', e => { if (e.target === modal) closeModal(modal); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') closeModal(modal);
    });
    return modal;
  }

  function findSizeLegend() {
    const fieldsets = document.querySelectorAll('fieldset.product-form__input, .product-form__input');
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend, .form__label');
      if (!legend) continue;
      // textContent 内の最初の意味のある単語が「サイズ」「size」かどうか
      const txt = (legend.textContent || '').trim();
      if (/^サイズ|^size/i.test(txt)) return legend;
    }
    return null;
  }

  function buildSizeGuideAnchor() {
    const anchor = document.createElement('div');
    anchor.className = 'ns-size-guide-anchor';
    anchor.innerHTML = `
      <button type="button" class="ns-size-guide-trigger ns-size-guide-trigger--accent">
        <svg class="ns-size-guide-trigger__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M3 5h18v6H3zM3 13h18v6H3zM7 7v2M11 7v2M15 7v2M19 7v2M7 15v2M11 15v2M15 15v2M19 15v2"/>
        </svg>
        <span>サイズ表を見る</span>
      </button>
    `;
    anchor.querySelector('button').addEventListener('click', () => {
      const modal = document.querySelector('.ns-size-modal');
      if (!modal) return;
      updateSelectedSizeHighlight();
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    });
    return anchor;
  }

  // ボタンを「サイズ」legend の中（右側）に配置
  // Dawn が variant 切替時に variant-selects を innerHTML 置換しても、reapply で確実に再挿入する
  function ensureSizeGuideAnchor(sizeChart, data) {
    const sizeLegend = findSizeLegend();
    if (!sizeLegend) return;

    // legend 内に既に anchor があるか確認
    const existing = sizeLegend.querySelector(':scope > .ns-size-guide-anchor');
    if (!existing) {
      const anchor = buildSizeGuideAnchor();
      sizeLegend.appendChild(anchor);
    }
    // 旧コードパスで作られた legend 外の anchor は除去（重複防止）
    document.querySelectorAll('.ns-size-guide-anchor').forEach(el => {
      if (!el.closest('legend, .form__label')) el.remove();
    });
  }

  function closeModal(modal) {
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function buildSizeModal(sizeChart, data) {
    const modal = document.createElement('div');
    modal.className = 'ns-size-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'ns-size-modal-title');

    const introHtml = renderSizeGuideIntroHtml(data);
    const tableHtml = renderSizeTableHtml(sizeChart, data);
    modal.innerHTML = `
      <div class="ns-size-modal__panel">
        <header class="ns-size-modal__header">
          <h2 class="ns-size-modal__title" id="ns-size-modal-title">サイズの選び方</h2>
          <button type="button" class="ns-size-modal__close" aria-label="閉じる">×</button>
        </header>
        <div class="ns-size-modal__body">
          ${introHtml}
          <h3 class="ns-size-modal__section-heading">採寸表 (cm)</h3>
          ${tableHtml}
          <p class="ns-size-modal__note">※ ±2cm 程度の誤差が生じる場合があります。</p>
        </div>
      </div>
    `;
    return modal;
  }

  // サイズ選びのコツを解説するイントロパネル。
  // 「数字だけ見ても自分に合うか分からない」問題への対処（用語の意味・普段サイズ
  // との目安マッピング・フィット感）を、商品ごとの custom.size_intro メタフィールド
  // （JSON）から描画する。
  //
  // ⚠️ メタフィールドが無い／壊れている場合は何も描画しない（空文字を返す）。
  //    別商品・別カテゴリの内容（例：ポロなのに「このシャツ」）を誤って表示しない
  //    ため、ハードコードのフォールバック文言は意図的に持たない。
  function renderSizeGuideIntroHtml(data) {
    const intro = data && data.metafields ? data.metafields.size_intro : null;
    if (!intro || typeof intro !== 'object') return '';

    let html = '<section class="ns-size-intro">';

    if (intro.lead) {
      html += `<p class="ns-size-intro__lead">${escapeHtml(intro.lead)}</p>`;
    }

    // bullets は <strong> 等の強調を含む信頼済みテンプレ文言。HTML をそのまま使う。
    const bullets = Array.isArray(intro.bullets) ? intro.bullets : [];
    if (bullets.length > 0) {
      html += '<ul class="ns-size-intro__bullets">';
      for (const b of bullets) {
        if (typeof b === 'string' && b) html += `<li>${b}</li>`;
      }
      html += '</ul>';
    }

    const legend = Array.isArray(intro.legend) ? intro.legend : [];
    if (legend.length > 0) {
      html += '<dl class="ns-size-intro__legend">';
      for (const item of legend) {
        if (!item || !item.term) continue;
        html += `<div><dt>${escapeHtml(item.term)}</dt><dd>${escapeHtml(item.desc || '')}</dd></div>`;
      }
      html += '</dl>';
    }

    const map = intro.size_map;
    const rows = map && Array.isArray(map.rows) ? map.rows : [];
    if (rows.length > 0) {
      const usualLabel   = escapeHtml(map.usual_label   || '普段の日本サイズ');
      const productLabel = escapeHtml(map.product_label || 'この商品のサイズ');
      html += `<table class="ns-size-intro__map"><thead><tr><th>${usualLabel}</th><th>${productLabel}</th></tr></thead><tbody>`;
      for (const r of rows) {
        if (!r) continue;
        html += `<tr><td>${escapeHtml(r.usual || '')}</td><td>${escapeHtml(r.product || '')}</td></tr>`;
      }
      html += '</tbody></table>';
    }

    html += '</section>';
    return html;
  }

  function renderSizeTableHtml(sizeChart, data) {
    const activeSizes = collectActiveSizes(data);
    const allSizes = sizeChart.available_sizes || [];
    const sizes = allSizes.filter(s => activeSizes.size === 0 || activeSizes.has(s));
    const table = sizeChart.size_tables.find(t => t.type === 'measure_yourself') || sizeChart.size_tables[0];

    let html = `<div class="ns-size-grid">`;
    for (const m of (table.measurements || [])) {
      const labelJp = translateMeasureLabel(m.type_label);
      html += `<section class="ns-size-measure">`;
      html += `<h3 class="ns-size-measure__heading">${escapeHtml(labelJp)}</h3>`;
      html += `<dl class="ns-size-measure__cells">`;
      for (const s of sizes) {
        const found = (m.values || []).find(v => v.size === s);
        let cell = '—';
        if (found) {
          if (found.value != null) cell = formatNumber(found.value);
          else if (found.min_value != null && found.max_value != null) {
            cell = `${formatNumber(found.min_value)}–${formatNumber(found.max_value)}`;
          }
        }
        html += `
          <div class="ns-size-measure__cell" data-size="${escapeHtml(s)}">
            <dt class="ns-size-measure__size">${escapeHtml(s)}</dt>
            <dd class="ns-size-measure__value">${cell}</dd>
          </div>
        `;
      }
      html += `</dl></section>`;
    }
    html += '</div>';
    return html;
  }

  function formatNumber(v) {
    const n = parseFloat(v);
    if (isNaN(n)) return v;
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  function collectActiveSizes(data) {
    const sizes = new Set();
    const opts = data.options || [];
    for (const opt of opts) {
      const n = (opt.name || '').toLowerCase();
      if (n.includes('サイズ') || n.includes('size')) {
        for (const val of (opt.values || [])) {
          if (typeof val === 'string') sizes.add(val);
          else if (val?.title) sizes.add(val.title);
          else if (val?.name) sizes.add(val.name);
        }
      }
    }
    return sizes;
  }

  function updateSelectedSizeHighlight() {
    const selectedSize = getSelectedSize();
    document.querySelectorAll('.ns-size-measure__cell[data-size]').forEach(cell => {
      cell.classList.toggle('is-current', selectedSize && cell.dataset.size === selectedSize);
    });
  }

  function getSelectedSize() {
    const fieldsets = document.querySelectorAll('.product-form__input fieldset, .product-form__input');
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend, label.form__label');
      if (!legend) continue;
      if (!/^サイズ|^size/i.test(legend.textContent.trim())) continue;
      const r = fs.querySelector('input[type="radio"]:checked');
      if (r?.value) return r.value;
      const sel = fs.querySelector('select');
      if (sel?.value) return sel.value;
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // 6.5 かんたんサイズチェック（外部SaaS不使用の自前実装。Phase 2b → 2026-07-18 大刷新）
  //    サイズ交換ができない受注生産のため、「間違えないサイズ」を先回りで提案する:
  //      モード0「身長・体重から」   … 身長・体重・体格から胸囲を推定し、size_chart の
  //                                    measure_yourself（各サイズの対応胸囲レンジ＝Printful実寸）と
  //                                    突合して推奨。推定値と根拠レンジを明示する
  //      モード1「普段のサイズから」 … size_intro.size_map（普段の日本サイズ→この商品のサイズ）
  //                                    ＋フィット好みで推奨サイズと根拠(実寸)を提示
  //      モード2「手持ちの服と比べる」… size_chart の product_measure（平置き実寸: 着丈/身幅）と
  //                                    手持ちの服の実寸を突き合わせ、サイズ別の差分を表示
  //    データが無いモードは出さない（キャップ等 size_chart 無し商品は導線ごと非表示）。
  //    モード0の推定式は日本人成人の平均体型からの近似。あくまで「目安」と明示し、
  //    サイズ決定の根拠には必ず商品側の実寸レンジ（Printful取得値）を添える。
  //    入力値は sessionStorage に保存し、同一タブ内の別商品でだけ再利用する
  //    （モーダルの「データは一切残りません」表示と実態を揃える。2026-07-18）。
  // -------------------------------------------------------------------------
  const SIZECHECK_STORE_KEY = 'ns_sizecheck_v1';
  const sizeCheckState = { usual: null, fit: 'standard', ownLen: '', ownWid: '', height: '', weight: '', build: 'standard' };

  function loadSizeCheckState() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SIZECHECK_STORE_KEY) || 'null');
      if (saved && typeof saved === 'object') Object.assign(sizeCheckState, saved);
    } catch (_) {}
  }
  function saveSizeCheckState() {
    try { sessionStorage.setItem(SIZECHECK_STORE_KEY, JSON.stringify(sizeCheckState)); } catch (_) {}
  }

  function sizeCheckModes(data) {
    const meta = data.metafields || {};
    const chart = meta.size_chart;
    if (!chart || !(chart.size_tables || []).length) return null;
    if (collectActiveSizes(data).size === 0) return null;

    const mapRows = meta.size_intro?.size_map?.rows;
    const usualMode = Array.isArray(mapRows) && mapRows.length > 0;

    const pm = (chart.size_tables || []).find(t => t.type === 'product_measure');
    const pmLen = pm && (pm.measurements || []).find(m => /^(Length|Length \(HSP\)|Body Length)$/i.test(m.type_label || ''));
    const pmWid = pm && (pm.measurements || []).find(m => /^(Width|Chest Width)$/i.test(m.type_label || ''));
    const compareMode = !!(pmLen && pmWid);

    // モード0: measure_yourself の胸囲（身体採寸レンジ）がある商品のみ
    const my = (chart.size_tables || []).find(t => t.type === 'measure_yourself');
    const myChest = my && (my.measurements || []).find(m => /^(Chest|Bust)$/i.test(m.type_label || ''));
    const myLen = my && (my.measurements || []).find(m => /^(Length|Length \(HSP\)|Body Length)$/i.test(m.type_label || ''));
    const bodyMode = !!myChest;

    if (!usualMode && !compareMode && !bodyMode) return null;
    return { usualMode, compareMode, bodyMode, mapRows: usualMode ? mapRows : [], pmLen, pmWid, myChest, myLen: myLen || pmLen };
  }

  // 採寸値 1 セルを { mid: 数値(範囲は中央), text: 表示用 } に正規化
  function measureValueFor(m, size) {
    const f = (m?.values || []).find(v => v.size === size);
    if (!f) return null;
    if (f.value != null) {
      const n = parseFloat(f.value);
      return isNaN(n) ? null : { mid: n, text: formatNumber(f.value) };
    }
    if (f.min_value != null && f.max_value != null) {
      const a = parseFloat(f.min_value); const b = parseFloat(f.max_value);
      if (isNaN(a) || isNaN(b)) return null;
      return { mid: (a + b) / 2, text: `${formatNumber(f.min_value)}–${formatNumber(f.max_value)}` };
    }
    return null;
  }

  // 採寸値 1 セルを { min, max, mid, text } に正規化（単一値は min=max）
  function measureRangeFor(m, size) {
    const f = (m?.values || []).find(v => v.size === size);
    if (!f) return null;
    if (f.value != null) {
      const n = parseFloat(f.value);
      return isNaN(n) ? null : { min: n, max: n, mid: n, text: formatNumber(f.value) };
    }
    if (f.min_value != null && f.max_value != null) {
      const a = parseFloat(f.min_value); const b = parseFloat(f.max_value);
      if (isNaN(a) || isNaN(b)) return null;
      return { min: Math.min(a, b), max: Math.max(a, b), mid: (a + b) / 2, text: `${formatNumber(f.min_value)}–${formatNumber(f.max_value)}` };
    }
    return null;
  }

  // モード0: 身長・体重・体格から胸囲を推定（日本人成人の平均体型からの近似・目安）。
  // 基準: 171cm・BMI23 で胸囲92cm。BMI±1 ≒ ±2.5cm、身長±1cm ≒ ±0.25cm、体格で ±3〜4cm 補正。
  function estimateChest(heightCm, weightKg, build) {
    const h = parseFloat(heightCm); const w = parseFloat(weightKg);
    if (isNaN(h) || isNaN(w) || h < 120 || h > 220 || w < 25 || w > 200) return null;
    const bmi = w / Math.pow(h / 100, 2);
    const buildAdj = build === 'slim' ? -3 : build === 'solid' ? 4 : 0;
    const chest = 92 + (bmi - 23) * 2.5 + (h - 171) * 0.25 + buildAdj;
    return Math.round(chest * 10) / 10;
  }

  // モード0の推奨ロジック。推定胸囲を各サイズの対応胸囲レンジ（measure_yourself＝Printful実寸）と突合。
  function recommendSizeByBody(data, modes, estChest, fit) {
    const chart = data.metafields.size_chart;
    const all = chart.available_sizes || [];
    const cands = all
      .map(s => ({ size: s, range: measureRangeFor(modes.myChest, s) }))
      .filter(c => c.range);
    if (!cands.length) return null;

    // 推定胸囲を受け止められる最小サイズを基準にする（サイズ順は Printful の昇順を信頼）
    let baseIdx = cands.findIndex(c => estChest <= c.range.max + 0.05);
    let capped = false;
    if (baseIdx < 0) { baseIdx = cands.length - 1; capped = true; }

    let idx = baseIdx;
    let note;
    if (capped) {
      note = `推定胸囲が対応レンジの上限を超えるため、最大の ${cands[idx].size} をご案内しています。ゆとりは少なめです。`;
    } else if (fit === 'tight') {
      const r = cands[baseIdx].range;
      const lowerThird = r.min + (r.max - r.min) * 0.4;
      if (baseIdx > 0 && estChest <= lowerThird && estChest >= cands[baseIdx - 1].range.min) {
        idx = baseIdx - 1;
        note = `タイトに着たい方向けに、標準の目安${cands[baseIdx].size}から1つ下げています。肩まわりが気になる方は${cands[baseIdx].size}が安心です。`;
      } else {
        note = '体に沿わせて着られる、ジャストの対応サイズです。';
      }
    } else if (fit === 'loose') {
      if (baseIdx < cands.length - 1) {
        idx = baseIdx + 1;
        note = `ゆったり着たい方向けに、標準の目安${cands[baseIdx].size}から1つ上げています。`;
      } else {
        note = `${cands[idx].size}がこの商品の最大サイズです。`;
      }
    } else {
      note = '推定胸囲がこのサイズの対応レンジに収まっています。';
    }

    const pick = cands[idx];
    const facts = [`推定胸囲 約${formatNumber(estChest)}cm`, `${pick.size}の対応胸囲 ${pick.range.text}cm`];
    const lenV = modes.myLen ? measureValueFor(modes.myLen, pick.size) : null;
    if (lenV) facts.push(`着丈 ${lenV.text}cm`);
    return { size: pick.size, note, facts };
  }

  // "S 〜 M" / "2XL" のような文字列から available_sizes に実在するサイズだけを順序どおり抽出
  function parseSizeTokens(text, availableSizes) {
    const raw = String(text || '').split(/[^A-Za-z0-9]+/).filter(Boolean);
    const hits = [];
    for (const s of availableSizes) {
      if (raw.some(t => t.toLowerCase() === s.toLowerCase()) && !hits.includes(s)) hits.push(s);
    }
    return hits;
  }

  // モード1の推奨ロジック。size_map の対応（=正データ）を基準に、フィット好みで1段だけ動かす。
  function recommendSize(data, modes, usual, fit) {
    const chart = data.metafields.size_chart;
    const all = chart.available_sizes || [];
    const row = modes.mapRows.find(r => (r.usual || '') === usual);
    if (!row) return null;
    const tokens = parseSizeTokens(row.product, all);
    if (tokens.length === 0) return null;

    const base = tokens[tokens.length - 1]; // 対応表の上限側 = 標準の目安
    let size = base;
    let note = `普段${usual}の方の標準の目安です。`;
    if (fit === 'tight') {
      size = tokens[0];
      note = tokens.length > 1
        ? `対応目安${row.product}のうち小さい側です。タイトに着たい方向け。`
        : 'タイトに着たい方も、まずは標準の目安がおすすめです。';
    } else if (fit === 'loose') {
      const idx = all.indexOf(base);
      if (idx >= 0 && idx < all.length - 1) {
        size = all[idx + 1];
        note = `標準の目安${base}から1サイズ上げています。`;
      } else {
        note = `${base}がこの商品の最大サイズです。`;
      }
    }

    // 根拠となる実寸（measure_yourself 優先）を最大3項目
    const my = (chart.size_tables || []).find(t => t.type === 'measure_yourself') || chart.size_tables[0];
    const facts = [];
    for (const m of (my?.measurements || [])) {
      const v = measureValueFor(m, size);
      if (v) facts.push(`${translateMeasureLabel(m.type_label)} ${v.text}cm`);
      if (facts.length >= 3) break;
    }
    return { size, note, facts };
  }

  function buildSizeCheckEntry() {
    const entry = document.createElement('button');
    entry.type = 'button';
    entry.className = 'ns-sizecheck-entry';
    entry.setAttribute('aria-haspopup', 'dialog');
    entry.innerHTML = `
      <span class="ns-sizecheck-entry__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path d="M12 3a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z"/>
          <path d="M5.5 21v-2.4c0-3 2.9-5.1 6.5-5.1s6.5 2.1 6.5 5.1V21"/>
        </svg>
      </span>
      <span class="ns-sizecheck-entry__main">
        <span class="ns-sizecheck-entry__title">かんたんサイズチェック</span>
        <span class="ns-sizecheck-entry__sub">身長・体重を入れるだけで、間違えないサイズがわかります</span>
      </span>
      <span class="ns-sizecheck-entry__cta" aria-hidden="true">
        <span>診断する</span>
        <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 1.5 7.5 6 3 10.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
    `;
    return entry;
  }

  // 購入判断の直前（サイズ選択のすぐ下）に置く、サイズについての但し書き。
  // 2026-07-19 第17便・嶋崎さん承認（A案）。それまで顧客向けの交換可否の記載がサイト上に一切なかった。
  // 制約（docs/rules/copy_corrections.md 訂正14）:
  //   - 同一文言を USP / FV / PDP へ 3 連呼しない。ここ（購入判断の直前）だけを主とする
  //   - 否定を先頭に置かない。肯定形（買う前に確かめられる）で始め、事実は小さく 1 回だけ添える
  //   - 訂正11: 理由づけに「受注生産」「一点ずつ作る」を使わない
  function buildSizeCheckNote() {
    const note = document.createElement('p');
    note.className = 'ns-sizecheck-note';
    note.innerHTML = `
      <span class="ns-sizecheck-note__lead">サイズは、買う前に確かめられます。</span>
      <span class="ns-sizecheck-note__sub">サイズ交換は承っていないため、ご注文前にご確認ください。</span>
    `;
    return note;
  }

  // 導線ブロックを variant-selects の直後（＝Dawn の innerHTML 置換の外側）に置く
  function ensureSizeCheckEntry(data) {
    const modes = sizeCheckModes(data);
    if (!modes) return;
    if (document.querySelector('.ns-sizecheck-entry')) return;
    const anchor = document.querySelector('variant-selects, variant-radios');
    let parent = null; let ref = null;
    if (anchor?.parentNode) { parent = anchor.parentNode; ref = anchor.nextSibling; }
    else {
      const qw = findQuantityWrap();
      if (!qw?.parentNode) return;
      parent = qw.parentNode; ref = qw;
    }
    const block = document.createElement('div');
    block.className = 'ns-sizecheck-block';
    block.appendChild(buildSizeCheckEntry());
    block.appendChild(buildSizeCheckNote());
    parent.insertBefore(block, ref);
  }

  function setupSizeCheck(data) {
    const modes = sizeCheckModes(data);
    if (!modes) return;
    loadSizeCheckState();

    document.addEventListener('click', e => {
      if (!e.target.closest('.ns-sizecheck-entry')) return;
      e.preventDefault();
      openSizeCheckModal(data, modes);
    });
  }

  function openSizeCheckModal(data, modes) {
    const modal = ensureSizeCheckModal(data, modes);
    renderSizeCheckBody(data, modes);
    renderSizeCheckResult(data, modes);
    renderSizeCheckCompare(data, modes);
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeSizeCheckModal() {
    const modal = document.querySelector('.ns-sizecheck-modal');
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function ensureSizeCheckModal(data, modes) {
    let modal = document.querySelector('.ns-sizecheck-modal');
    if (modal) return modal;

    const usualChips = modes.mapRows.map(r => `
      <button type="button" class="ns-sizecheck-chip" data-usual="${escapeHtml(r.usual || '')}" role="radio" aria-checked="false">${escapeHtml(r.usual || '')}</button>
    `).join('');
    const fitChipsHtml = () => [
      ['tight', 'ぴったりめ'], ['standard', '標準'], ['loose', 'ゆったりめ'],
    ].map(([key, label]) => `
      <button type="button" class="ns-sizecheck-chip" data-fit="${key}" role="radio" aria-checked="false">${label}</button>
    `).join('');
    const buildChips = [
      ['slim', 'ほっそり'], ['standard', '標準'], ['solid', 'がっしり'],
    ].map(([key, label]) => `
      <button type="button" class="ns-sizecheck-chip" data-build="${key}" role="radio" aria-checked="false">${label}</button>
    `).join('');

    const tabButtons = [];
    if (modes.bodyMode) tabButtons.push('<button type="button" class="ns-sizecheck-tab" data-tab="body" role="tab">身長・体重から</button>');
    if (modes.usualMode) tabButtons.push('<button type="button" class="ns-sizecheck-tab" data-tab="usual" role="tab">普段のサイズから</button>');
    if (modes.compareMode) tabButtons.push('<button type="button" class="ns-sizecheck-tab" data-tab="compare" role="tab">手持ちの服と比べる</button>');

    modal = document.createElement('div');
    modal.className = 'ns-sizecheck-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'ns-sizecheck-title');
    modal.innerHTML = `
      <div class="ns-sizecheck-modal__panel">
        <header class="ns-sizecheck-modal__header">
          <h2 class="ns-sizecheck-modal__title" id="ns-sizecheck-title">かんたんサイズチェック</h2>
          <button type="button" class="ns-sizecheck-modal__close" aria-label="閉じる">×</button>
        </header>
        <div class="ns-sizecheck-modal__body">
          ${tabButtons.length > 1 ? `<div class="ns-sizecheck-modal__tabs" role="tablist">${tabButtons.join('')}</div>` : ''}

          ${modes.bodyMode ? `
          <section class="ns-sizecheck-pane" data-pane="body">
            <p class="ns-sizecheck-q">身長と体重を入力ください(データは一切残りません)。</p>
            <div class="ns-sizecheck-own">
              <label class="ns-sizecheck-own__field">
                <span class="ns-sizecheck-own__label">身長</span>
                <span class="ns-sizecheck-own__input"><input type="number" inputmode="decimal" step="1" min="120" max="220" data-ns-body-height> cm</span>
              </label>
              <label class="ns-sizecheck-own__field">
                <span class="ns-sizecheck-own__label">体重</span>
                <span class="ns-sizecheck-own__input"><input type="number" inputmode="decimal" step="1" min="25" max="200" data-ns-body-weight> kg</span>
              </label>
            </div>
            <p class="ns-sizecheck-q">体格は？</p>
            <div class="ns-sizecheck-chips" data-ns-build role="radiogroup" aria-label="体格">${buildChips}</div>
            <p class="ns-sizecheck-q">着心地の好みは？</p>
            <div class="ns-sizecheck-chips" data-ns-fit role="radiogroup" aria-label="着心地の好み">${fitChipsHtml()}</div>
            <div class="ns-sizecheck-result-wrap" data-ns-body-result></div>
          </section>` : ''}

          ${modes.usualMode ? `
          <section class="ns-sizecheck-pane" data-pane="usual">
            <p class="ns-sizecheck-q">普段着ている日本サイズは？</p>
            <div class="ns-sizecheck-chips" data-ns-usual role="radiogroup" aria-label="普段の日本サイズ">${usualChips}</div>
            <p class="ns-sizecheck-q">着心地の好みは？</p>
            <div class="ns-sizecheck-chips" data-ns-fit role="radiogroup" aria-label="着心地の好み">${fitChipsHtml()}</div>
            <div class="ns-sizecheck-result-wrap" data-ns-result></div>
          </section>` : ''}

          ${modes.compareMode ? `
          <section class="ns-sizecheck-pane" data-pane="compare">
            <p class="ns-sizecheck-q">手持ちの近い服(Tシャツ等)を平らに置いて測ってください。</p>
            <div class="ns-sizecheck-own">
              <label class="ns-sizecheck-own__field">
                <span class="ns-sizecheck-own__label">着丈(襟の付け根〜裾)</span>
                <span class="ns-sizecheck-own__input"><input type="number" inputmode="decimal" step="0.5" min="30" max="120" data-ns-own-len> cm</span>
              </label>
              <label class="ns-sizecheck-own__field">
                <span class="ns-sizecheck-own__label">身幅(脇下の左右を直線で)</span>
                <span class="ns-sizecheck-own__input"><input type="number" inputmode="decimal" step="0.5" min="25" max="100" data-ns-own-wid> cm</span>
              </label>
            </div>
            <div class="ns-sizecheck-compare" data-ns-compare></div>
          </section>` : ''}

          <p class="ns-sizecheck-modal__note">診断は目安です。正確な寸法は<button type="button" class="ns-sizecheck-modal__sizetable-link" data-ns-open-sizetable>採寸表</button>でご確認いただけます。</p>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    // 開閉
    modal.querySelector('.ns-sizecheck-modal__close').addEventListener('click', closeSizeCheckModal);
    modal.addEventListener('click', e => { if (e.target === modal) closeSizeCheckModal(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') closeSizeCheckModal();
    });

    // タブ切替
    const setTab = (name) => {
      modal.querySelectorAll('.ns-sizecheck-tab').forEach(t => t.classList.toggle('is-active', t.dataset.tab === name));
      modal.querySelectorAll('.ns-sizecheck-pane').forEach(p => p.classList.toggle('is-active', p.dataset.pane === name));
    };
    modal.querySelectorAll('.ns-sizecheck-tab').forEach(tab => {
      tab.addEventListener('click', () => setTab(tab.dataset.tab));
    });
    setTab(modes.bodyMode ? 'body' : (modes.usualMode ? 'usual' : 'compare'));

    // チップ選択（普段サイズ・体格・着心地は modal 全体で委譲。fit は両ペインの状態を同期）
    modal.addEventListener('click', e => {
      const chip = e.target.closest('.ns-sizecheck-chip');
      if (!chip) return;
      if (chip.dataset.usual != null) sizeCheckState.usual = chip.dataset.usual;
      else if (chip.dataset.build != null) sizeCheckState.build = chip.dataset.build;
      else if (chip.dataset.fit != null) sizeCheckState.fit = chip.dataset.fit;
      else return;
      saveSizeCheckState();
      renderSizeCheckBody(data, modes);
      renderSizeCheckResult(data, modes);
    });

    // モード0: 身長・体重入力
    const heightInput = modal.querySelector('[data-ns-body-height]');
    const weightInput = modal.querySelector('[data-ns-body-weight]');
    if (heightInput) heightInput.value = sizeCheckState.height || '';
    if (weightInput) weightInput.value = sizeCheckState.weight || '';
    [heightInput, weightInput].forEach(input => {
      input?.addEventListener('input', () => {
        sizeCheckState.height = heightInput?.value || '';
        sizeCheckState.weight = weightInput?.value || '';
        saveSizeCheckState();
        renderSizeCheckBody(data, modes);
      });
    });

    // モード2: 実寸入力
    const lenInput = modal.querySelector('[data-ns-own-len]');
    const widInput = modal.querySelector('[data-ns-own-wid]');
    if (lenInput) lenInput.value = sizeCheckState.ownLen || '';
    if (widInput) widInput.value = sizeCheckState.ownWid || '';
    [lenInput, widInput].forEach(input => {
      input?.addEventListener('input', () => {
        sizeCheckState.ownLen = lenInput?.value || '';
        sizeCheckState.ownWid = widInput?.value || '';
        saveSizeCheckState();
        renderSizeCheckCompare(data, modes);
      });
    });

    // サイズ表リンク・サイズ選択（委譲）
    modal.addEventListener('click', e => {
      if (e.target.closest('[data-ns-open-sizetable]')) {
        closeSizeCheckModal();
        const sizeModal = document.querySelector('.ns-size-modal');
        if (sizeModal) {
          updateSelectedSizeHighlight();
          sizeModal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        }
        return;
      }
      const pick = e.target.closest('[data-ns-pick-size]');
      if (pick) {
        selectColorAndSize(null, pick.dataset.nsPickSize);
        closeSizeCheckModal();
      }
    });

    return modal;
  }

  function renderSizeCheckBody(data, modes) {
    if (!modes.bodyMode) return;
    const modal = document.querySelector('.ns-sizecheck-modal');
    if (!modal) return;
    const wrap = modal.querySelector('[data-ns-body-result]');
    if (!wrap) return;

    // 体格チップの選択状態を同期（fit チップは renderSizeCheckResult 側と共通処理）
    modal.querySelectorAll('[data-ns-build] .ns-sizecheck-chip').forEach(c => {
      const on = c.dataset.build === (sizeCheckState.build || 'standard');
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    modal.querySelectorAll('[data-ns-fit] .ns-sizecheck-chip').forEach(c => {
      const on = c.dataset.fit === sizeCheckState.fit;
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    const estChest = estimateChest(sizeCheckState.height, sizeCheckState.weight, sizeCheckState.build || 'standard');
    if (estChest == null) {
      wrap.innerHTML = '<p class="ns-sizecheck-placeholder">身長と体重を入れると、おすすめサイズを表示します。</p>';
      return;
    }
    const rec = recommendSizeByBody(data, modes, estChest, sizeCheckState.fit);
    if (!rec) {
      wrap.innerHTML = '<p class="ns-sizecheck-placeholder">この商品の対応胸囲データがありません。採寸表をご確認ください。</p>';
      return;
    }
    wrap.innerHTML = `
      <div class="ns-sizecheck-result">
        <span class="ns-sizecheck-result__size">${escapeHtml(rec.size)}</span>
        <div class="ns-sizecheck-result__body">
          <p class="ns-sizecheck-result__title">おすすめは ${escapeHtml(rec.size)} サイズ</p>
          ${rec.facts.length ? `<p class="ns-sizecheck-result__facts">${escapeHtml(rec.facts.join('・'))}</p>` : ''}
          <p class="ns-sizecheck-result__note">${escapeHtml(rec.note)}</p>
          <button type="button" class="ns-sizecheck-result__pick" data-ns-pick-size="${escapeHtml(rec.size)}">このサイズを選ぶ</button>
        </div>
      </div>
    `;
  }

  function renderSizeCheckResult(data, modes) {
    if (!modes.usualMode) return;
    const modal = document.querySelector('.ns-sizecheck-modal');
    if (!modal) return;
    const wrap = modal.querySelector('[data-ns-result]');
    if (!wrap) return;

    // チップの選択状態を同期
    modal.querySelectorAll('[data-ns-usual] .ns-sizecheck-chip').forEach(c => {
      const on = c.dataset.usual === sizeCheckState.usual;
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    modal.querySelectorAll('[data-ns-fit] .ns-sizecheck-chip').forEach(c => {
      const on = c.dataset.fit === sizeCheckState.fit;
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    if (!sizeCheckState.usual) {
      wrap.innerHTML = '<p class="ns-sizecheck-placeholder">普段のサイズを選ぶと、おすすめサイズを表示します。</p>';
      return;
    }
    const rec = recommendSize(data, modes, sizeCheckState.usual, sizeCheckState.fit);
    if (!rec) {
      wrap.innerHTML = '<p class="ns-sizecheck-placeholder">この組み合わせの目安データがありません。採寸表をご確認ください。</p>';
      return;
    }
    wrap.innerHTML = `
      <div class="ns-sizecheck-result">
        <span class="ns-sizecheck-result__size">${escapeHtml(rec.size)}</span>
        <div class="ns-sizecheck-result__body">
          <p class="ns-sizecheck-result__title">おすすめは ${escapeHtml(rec.size)} サイズ</p>
          ${rec.facts.length ? `<p class="ns-sizecheck-result__facts">${escapeHtml(rec.facts.join('・'))}</p>` : ''}
          <p class="ns-sizecheck-result__note">${escapeHtml(rec.note)}</p>
          <button type="button" class="ns-sizecheck-result__pick" data-ns-pick-size="${escapeHtml(rec.size)}">このサイズを選ぶ</button>
        </div>
      </div>
    `;
  }

  function renderSizeCheckCompare(data, modes) {
    if (!modes.compareMode) return;
    const modal = document.querySelector('.ns-sizecheck-modal');
    if (!modal) return;
    const wrap = modal.querySelector('[data-ns-compare]');
    if (!wrap) return;

    const ownLen = parseFloat(sizeCheckState.ownLen);
    const ownWid = parseFloat(sizeCheckState.ownWid);
    const hasLen = !isNaN(ownLen);
    const hasWid = !isNaN(ownWid);
    if (!hasLen && !hasWid) {
      wrap.innerHTML = '<p class="ns-sizecheck-placeholder">実寸を入力すると、サイズごとの差分を表示します。</p>';
      return;
    }

    const chart = data.metafields.size_chart;
    const sizes = chart.available_sizes || [];
    const diffText = (d, kind) => {
      if (Math.abs(d) <= 1) return '<span class="ns-sizecheck-diff ns-sizecheck-diff--same">ほぼ同じ</span>';
      const word = kind === 'len' ? (d > 0 ? '長い' : '短い') : (d > 0 ? '広い' : '細い');
      const cls = d > 0 ? 'plus' : 'minus';
      return `<span class="ns-sizecheck-diff ns-sizecheck-diff--${cls}">${formatNumber(Math.abs(d).toFixed(1))}cm ${word}</span>`;
    };

    const rows = [];
    for (const s of sizes) {
      const lenV = measureValueFor(modes.pmLen, s);
      const widV = measureValueFor(modes.pmWid, s);
      if (!lenV && !widV) continue;
      let score = 0; let n = 0;
      let dLen = null; let dWid = null;
      if (hasLen && lenV) { dLen = lenV.mid - ownLen; score += Math.abs(dLen); n++; }
      if (hasWid && widV) { dWid = widV.mid - ownWid; score += Math.abs(dWid) * 1.4; n++; } // 身幅はフィット影響が大きいため重み付け
      if (n === 0) continue;
      rows.push({ size: s, lenV, widV, dLen, dWid, score });
    }
    if (rows.length === 0) {
      wrap.innerHTML = '<p class="ns-sizecheck-placeholder">比較できる寸法データがありません。</p>';
      return;
    }
    const best = rows.reduce((a, b) => (b.score < a.score ? b : a), rows[0]);

    wrap.innerHTML = `
      <ul class="ns-sizecheck-compare__rows">
        ${rows.map(r => `
          <li>
            <button type="button" class="ns-sizecheck-compare__row${r === best ? ' is-best' : ''}" data-ns-pick-size="${escapeHtml(r.size)}">
              <span class="ns-sizecheck-compare__size">${escapeHtml(r.size)}</span>
              <span class="ns-sizecheck-compare__cells">
                ${r.dLen != null ? `<span class="ns-sizecheck-compare__cell">着丈 ${diffText(r.dLen, 'len')}</span>` : ''}
                ${r.dWid != null ? `<span class="ns-sizecheck-compare__cell">身幅 ${diffText(r.dWid, 'wid')}</span>` : ''}
              </span>
              ${r === best ? '<span class="ns-sizecheck-compare__badge">いちばん近い</span>' : ''}
            </button>
          </li>
        `).join('')}
      </ul>
      <p class="ns-sizecheck-compare__hint">手持ちの服との差分です。タップするとそのサイズを選択します。</p>
    `;
  }

  // -------------------------------------------------------------------------
  // 7. 商品タイトルを価格の隣（同じ行）に配置
  //    PC: ProductInfo 内、左にタイトル・右に価格
  //    SP: 同じ並び。横幅が足りなければ自動的に折り返す
  // -------------------------------------------------------------------------
  function pairTitleWithPrice() {
    const wrapper = document.querySelector('.product__info-wrapper');
    if (!wrapper) return;

    const title = wrapper.querySelector('.product__title');
    const price = wrapper.querySelector('[id^="price-"], .price');
    if (!title || !price) return;

    // 旧コード（画像の上にクローン）の名残を除去
    document.querySelectorAll('.ns-title-above').forEach(el => el.remove());
    title.classList.remove('ns-title-moved');

    // 既に row に入っているならスキップ
    const existingRow = wrapper.querySelector('.ns-title-price-row');
    if (existingRow && existingRow.contains(title) && existingRow.contains(price)) return;

    const row = existingRow || document.createElement('div');
    if (!existingRow) {
      row.className = 'ns-title-price-row';
      // タイトルの現在位置に row を挿入
      title.parentNode.insertBefore(row, title);
    }
    // タイトル → 価格 の順で row に入れる
    row.appendChild(title);
    row.appendChild(price);
  }

  // -------------------------------------------------------------------------
  // 8. Sticky add-to-cart bar
  //    - 表示タイミング：Add ボタンの上端が viewport の上から 60% より上に
  //      上がったら表示（早めに登場）。スクロール下で素早く現れる。
  //    - PC: max-width で中央寄せ + バランス良い 3 ペイン構成
  //    - SP: タイトル/価格大きめ、ボタン横幅 50%
  // -------------------------------------------------------------------------
  function setupStickyCart(data) {
    if (document.querySelector('.ns-sticky-cart')) return;

    const productForm = document.querySelector('product-form, .product-form');
    if (!productForm) return;

    const addBtn = productForm.querySelector('button[name="add"], button.product-form__submit');
    if (!addBtn) return;

    const titleText = data.title || document.querySelector('.product__title h1, .product__title .h1')?.textContent?.trim() || '';
    const priceEl = document.querySelector('.product__info-container .price-item--regular, .price__regular .price-item, .price .price-item');
    const priceText = priceEl?.textContent?.trim() || '';

    // size_chart データがある場合のみ「サイズ表を見る」ボタンを sticky bar 内にも表示
    const hasSizeChart = !!(data.metafields?.size_chart?.size_tables?.length);
    const sizeGuideHtml = hasSizeChart ? `
      <button type="button" class="ns-sticky-cart__size-guide ns-size-guide-trigger" data-ns-sticky-size aria-label="サイズ表を見る">
        <svg class="ns-size-guide-trigger__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M3 5h18v6H3zM3 13h18v6H3zM7 7v2M11 7v2M15 7v2M19 7v2M7 15v2M11 15v2M15 15v2M19 15v2"/>
        </svg>
        <span>サイズ表を見る</span>
      </button>
    ` : '';

    const bar = document.createElement('div');
    bar.className = 'ns-sticky-cart';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = `
      <div class="ns-sticky-cart__inner">
        ${sizeGuideHtml}
        <span class="ns-sticky-cart__titlebox">
          <span class="ns-sticky-cart__title">${escapeHtml(titleText)}</span>
          <span class="ns-sticky-cart__size" data-ns-sticky-variant hidden></span>
        </span>
        <button type="button" class="ns-sticky-cart__btn" data-ns-sticky-add>
          <span>カートに追加</span>
        </button>
        <span class="ns-sticky-cart__price" data-ns-sticky-price>${escapeHtml(priceText)}</span>
      </div>
    `;
    document.body.appendChild(bar);

    // 選択中サイズを商品名の右に常時表示する（サイズ交換に対応できないため、
    // カート追加の直前に選び間違いへ気付ける導線。2026-07-18 嶋崎さん指示）
    // サイズは省略枠（__title の ellipsis）の外の独立要素。長い商品名でも
    // サイズ側は絶対に切らず、商品名側だけを縮める。
    function selectedSizeText() {
      for (const fs of document.querySelectorAll('variant-selects fieldset, variant-radios fieldset')) {
        const legend = fs.querySelector('legend');
        if (!legend || !legend.textContent.includes('サイズ')) continue;
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
      }
      for (const wrap of document.querySelectorAll('variant-selects .product-form__input--dropdown')) {
        const label = wrap.querySelector('.form__label');
        if (!label || !label.textContent.includes('サイズ')) continue;
        const sel = wrap.querySelector('select');
        if (sel) return sel.value;
      }
      return '';
    }
    function updateStickySize() {
      const el = bar.querySelector('[data-ns-sticky-variant]');
      if (!el) return;
      const size = selectedSizeText();
      if (size && size !== 'One size') {
        el.textContent = size;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
      fitStickyTitle();
    }
    // 商品名＋サイズが 1 行に収まらない端末幅では、商品名のフォントを段階的に
    // 縮めて全表示を保証する（ellipsis は最終手段。下限 10px）
    function fitStickyTitle() {
      const titleEl = bar.querySelector('.ns-sticky-cart__title');
      if (!titleEl) return;
      titleEl.style.fontSize = '';
      let px = parseFloat(window.getComputedStyle(titleEl).fontSize) || 12;
      let guard = 12;
      while (titleEl.scrollWidth > titleEl.clientWidth + 1 && px > 10 && guard-- > 0) {
        px -= 0.5;
        titleEl.style.fontSize = px + 'px';
      }
    }
    updateStickySize();
    window.addEventListener('resize', fitStickyTitle);
    document.addEventListener('change', (e) => {
      if (e.target?.matches?.('input[type="radio"], select')) {
        updateStickySize();
        setTimeout(updateStickySize, 300);
      }
    }, true);

    // クリックで本物の Add ボタンを発火
    bar.querySelector('[data-ns-sticky-add]').addEventListener('click', () => {
      const realBtn = document.querySelector('product-form button[name="add"], .product-form button[name="add"]');
      if (realBtn) {
        realBtn.click();
        bar.classList.add('is-clicking');
        setTimeout(() => bar.classList.remove('is-clicking'), 800);
      }
    });

    // サイズ表ボタン → サイズモーダルを開く（インラインの ns-size-guide-anchor と同じ挙動）
    bar.querySelector('[data-ns-sticky-size]')?.addEventListener('click', () => {
      const modal = document.querySelector('.ns-size-modal');
      if (!modal) return;
      updateSelectedSizeHighlight();
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    });

    // スクロール検出：Add ボタンの位置で判定
    function updateVisibility() {
      const rect = addBtn.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Add ボタンの top が viewport の上から 60% より上に来たら表示
      // = ユーザがちょっとスクロールするだけで早めに登場する
      const showBar = rect.top < vh * 0.6;
      bar.setAttribute('aria-hidden', showBar ? 'false' : 'true');
    }
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = null; updateVisibility(); });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateVisibility);
    updateVisibility();

    // variant 切替で価格を更新
    const updatePrice = () => {
      const newPriceEl = document.querySelector('.product__info-container .price-item--regular, .price__regular .price-item, .price .price-item');
      const newPrice = newPriceEl?.textContent?.trim();
      if (newPrice) {
        const target = bar.querySelector('[data-ns-sticky-price]');
        if (target) target.textContent = newPrice;
      }
    };
    document.addEventListener('change', e => {
      if (e.target?.matches('input[type="radio"], select')) {
        setTimeout(updatePrice, 300);
      }
    }, true);
  }

  // -------------------------------------------------------------------------
  // 8.4 配送目安モーダル（サイズ別 × カラー別 一覧）
  //    .ns-delivery-info をクリック、または説明欄内 [data-ns-delivery-trigger]
  //    から開く。サイズタブを起点に、各カラーを「日本拠点(早い)→ 海外拠点 →
  //    在庫なし」順でグループ表示。行クリックでそのカラー・サイズを即選択。
  // -------------------------------------------------------------------------
  function setupDeliveryGuide(data) {
    if (!data) return;
    ensureDeliveryModal(data);

    // クリック委譲：.ns-delivery-info（数量フォーム右）と
    // 説明欄内の [data-ns-delivery-trigger] の両方を受ける。
    document.addEventListener('click', e => {
      const trigger = e.target.closest('.ns-delivery-info, [data-ns-delivery-trigger]');
      if (!trigger) return;
      e.preventDefault();
      openDeliveryModal(data);
    });
    document.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const trigger = e.target.closest('.ns-delivery-info, [data-ns-delivery-trigger]');
      if (!trigger) return;
      e.preventDefault();
      openDeliveryModal(data);
    });
  }

  function ensureDeliveryModal(data) {
    if (document.querySelector('.ns-delivery-modal')) return;

    const sizes = Array.from(collectActiveSizes(data));
    const hasSizes = sizes.length > 0;
    const sizeTabs = sizes.map(s =>
      `<button type="button" class="ns-delivery-modal__size-tab" data-size="${escapeHtml(s)}" role="tab">${escapeHtml(s)}</button>`
    ).join('');

    const modal = document.createElement('div');
    modal.className = 'ns-delivery-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'ns-delivery-modal-title');
    modal.innerHTML = `
      <div class="ns-delivery-modal__panel">
        <header class="ns-delivery-modal__header">
          <h2 class="ns-delivery-modal__title" id="ns-delivery-modal-title">サイズ・カラー別 お届け目安</h2>
          <button type="button" class="ns-delivery-modal__close" aria-label="閉じる">×</button>
        </header>
        <div class="ns-delivery-modal__body">
          <p class="ns-delivery-modal__intro">${hasSizes
            ? 'サイズを選ぶと、各カラーのお届け目安を早く届く順に表示します。カラーはタップで選択できます。'
            : 'カラーごとのお届け目安です。タップで選択できます。'}</p>
          ${hasSizes ? `<div class="ns-delivery-modal__sizes" role="tablist">${sizeTabs}</div>` : ''}
          <div class="ns-delivery-modal__list" data-ns-delivery-list></div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    const close = () => {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };
    modal.querySelector('.ns-delivery-modal__close').addEventListener('click', close);
    modal.addEventListener('click', e => { if (e.target === modal) close(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') close();
    });

    modal.querySelectorAll('.ns-delivery-modal__size-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        modal.querySelectorAll('.ns-delivery-modal__size-tab').forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        renderDeliveryList(data, tab.dataset.size);
      });
    });
  }

  function openDeliveryModal(data) {
    const modal = document.querySelector('.ns-delivery-modal');
    if (!modal) return;
    const tabs = modal.querySelectorAll('.ns-delivery-modal__size-tab');
    if (tabs.length === 0) {
      // サイズ次元が無い商品（One size のキャップ等）: カラー一覧を直接描画
      renderDeliveryList(data, null);
    } else {
      const currentSize = getSelectedSize();
      let target = null;
      tabs.forEach(t => { if (t.dataset.size === currentSize) target = t; });
      (target || tabs[0])?.click();
    }
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function renderDeliveryList(data, size) {
    const modal = document.querySelector('.ns-delivery-modal');
    if (!modal || !data) return;
    const listEl = modal.querySelector('[data-ns-delivery-list]');
    if (!listEl) return;

    const fulfillmentMap = data.metafields?.variant_fulfillment?.map || {};
    const swatches = data.metafields?.color_swatches?.swatches || [];
    const jpDays = data.metafields?.delivery_japan_days || '2〜5 営業日';
    const ovDays = data.metafields?.delivery_overseas_days || '8〜14 営業日';
    const swatchByEn = new Map(swatches.map(s => [s.name_en, s]));

    // このサイズに紐付く既存 variant をカラーごとに索引化
    // size が null（One size 等でサイズ次元が無い商品）は全 variant を対象にする
    const sizeVariants = (size == null)
      ? (data.variants || [])
      : (data.variants || []).filter(v => (v.options || []).includes(size));
    const variantByColor = new Map();
    for (const v of sizeVariants) {
      for (const opt of (v.options || [])) {
        if (swatchByEn.has(opt)) { variantByColor.set(opt, v); break; }
      }
    }

    const rows = [];
    for (const sw of swatches) {
      const v = variantByColor.get(sw.name_en);
      if (v) {
        const pfId = v.sku ? extractPrintfulVariantId(v.sku) : null;
        const region = (pfId && fulfillmentMap[pfId]) || null;
        const available = v.available !== false && !!region;
        rows.push({
          colorEn: sw.name_en,
          colorJp: sw.name_jp || sw.name_en,
          hex: sw.hex || '#ccc',
          region: available ? region : null,
          days: region === 'japan' ? jpDays : (region === 'overseas' ? ovDays : null),
          available,
        });
      } else {
        // バリアント自体が存在しない（その色×サイズの組み合わせ未提供）
        rows.push({
          colorEn: sw.name_en,
          colorJp: sw.name_jp || sw.name_en,
          hex: sw.hex || '#ccc',
          region: null,
          days: null,
          available: false,
        });
      }
    }

    const groups = {
      japan:       rows.filter(r => r.region === 'japan'    && r.available),
      overseas:    rows.filter(r => r.region === 'overseas' && r.available),
      unavailable: rows.filter(r => !r.available),
    };

    let html = '';
    if (groups.japan.length > 0) {
      html += renderDeliveryGroup('japan', '早く届きます', jpDays, groups.japan, size);
    }
    if (groups.overseas.length > 0) {
      html += renderDeliveryGroup('overseas', '少しお時間いただきます', ovDays, groups.overseas, size);
    }
    if (groups.unavailable.length > 0) {
      html += renderDeliveryGroup('unavailable', '在庫がありません', null, groups.unavailable, size);
    }

    listEl.innerHTML = html || '<p class="ns-delivery-modal__empty">表示できるデータがありません。</p>';

    listEl.querySelectorAll('.ns-delivery-modal__row').forEach(row => {
      if (row.classList.contains('is-unavailable')) return;
      row.addEventListener('click', () => {
        selectColorAndSize(row.dataset.color, row.dataset.size);
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
      row.addEventListener('keydown', e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        row.click();
      });
    });
  }

  function renderDeliveryGroup(region, badge, days, rows, size) {
    const isUnavailable = region === 'unavailable';
    const items = rows.map(r => `
      <li class="ns-delivery-modal__row${isUnavailable ? ' is-unavailable' : ''}" data-region="${region}" data-color="${escapeHtml(r.colorEn)}" data-size="${escapeHtml(size || '')}"${isUnavailable ? '' : ' tabindex="0" role="button"'}>
        <span class="ns-color-circle ns-color-circle--lg" style="background:${escapeHtml(r.hex)};"${isLightColor(r.hex) ? ' data-light="true"' : ''}></span>
        <span class="ns-delivery-modal__row-label">${escapeHtml(r.colorJp)}</span>
        <span class="ns-delivery-modal__row-days">${isUnavailable ? '在庫なし' : escapeHtml(days)}</span>
      </li>
    `).join('');
    return `
      <section class="ns-delivery-modal__group ns-delivery-modal__group--${region}">
        <header class="ns-delivery-modal__group-heading">
          <span class="ns-delivery-modal__group-badge">${escapeHtml(badge)}</span>
          ${days ? `<span class="ns-delivery-modal__group-days">${escapeHtml(days)}</span>` : ''}
          <span class="ns-delivery-modal__group-count">${rows.length}色</span>
        </header>
        <ul class="ns-delivery-modal__rows">${items}</ul>
      </section>
    `;
  }

  function selectColorAndSize(color, size) {
    const fieldsets = document.querySelectorAll('.product-form__input fieldset, .product-form__input');
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend, label.form__label');
      if (!legend) continue;
      const t = (legend.textContent || '').trim();
      if (/色|color|colour/i.test(t)) {
        const r = fs.querySelector(`input[type="radio"][value="${color}"]`);
        if (r && !r.checked) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
          r.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else if (size && /^サイズ|^size/i.test(t)) {
        const r = fs.querySelector(`input[type="radio"][value="${size}"]`);
        if (r && !r.checked) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
          r.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // 8.45 「利用できません」→「在庫がありません」 文言置換
  //    Dawn の翻訳キー products.product.unavailable は window.variantStrings.unavailable
  //    に格納されている。 main-product.liquid の inline script で初期化された後、
  //    我々の defer スクリプトが上書きできる。
  //    既にレンダリング済みのボタンテキストも reapply 経由で都度パッチする。
  // -------------------------------------------------------------------------
  function customizeUnavailableLabel() {
    if (window.variantStrings) {
      window.variantStrings.unavailable = '在庫がありません';
    }
  }
  function patchUnavailableButtonText() {
    document.querySelectorAll('button[name="add"], button.product-form__submit').forEach(btn => {
      const span = btn.querySelector('span') || btn;
      if (span.textContent.trim() === '利用できません') {
        span.textContent = '在庫がありません';
      }
    });
  }

  // -------------------------------------------------------------------------
  // 8.5 Dawn のメディア再表示演出を抑止
  //    Dawn 14+ では <product-info> (ProductInfo クラス) が
  //    optionValueSelectionChange を購読し、handleUpdateProductInfo →
  //    this.updateMedia(html, featuredMediaId) → media-gallery.setActiveMedia()
  //    の経路でメディアを切替える。setActiveMedia 内では:
  //      - .is-active クラス付替（PC で見た目フェード相当）
  //      - parentElement.prepend() で DOM 並び替え（一瞬消える原因）
  //      - parent.scrollTo({ left }) で SP の横スライド
  //      - window.scrollTo({ behavior: 'smooth' }) でページスクロール
  //    が連続で走り、我々の swapMainImage（即時差し替え）と二重演出になる。
  //    そのため ProductInfo.prototype.updateMedia を no-op で上書きする。
  //    price/在庫/URL更新は別経路で動くので維持される。
  // -------------------------------------------------------------------------
  function disableDawnVariantMediaSwap() {
    const apply = (Cls) => {
      if (!Cls?.prototype) return;
      if (Cls.prototype.__nsMediaPatched) return;
      Cls.prototype.__nsMediaPatched = true;
      if (typeof Cls.prototype.updateMedia === 'function') {
        Cls.prototype.updateMedia = function () { /* no-op: handled by ns swapMainImage */ };
      }
    };
    // Dawn 14+: product-info が updateMedia を持つ
    // 旧 Dawn: variant-selects / variant-radios に updateMedia があるため後方互換で全て対象
    ['product-info', 'variant-selects', 'variant-radios'].forEach(name => {
      try {
        const existing = customElements.get?.(name);
        if (existing) apply(existing);
        if (customElements.whenDefined) {
          customElements.whenDefined(name)
            .then(() => apply(customElements.get(name)))
            .catch(() => {});
        }
      } catch (_) {}
    });
  }

  // -------------------------------------------------------------------------
  // 8.7 SP: バリアント選択をメイン画像の直下（サムネイル一覧の上）へ移動
  //     SP はバリアント選択までスクロールするとメイン画像が画面外になり、
  //     色を替えるたびに上下往復スクロールになる。画像のすぐ下に選択 UI を
  //     置けば、その場で swapMainImage の即時切替を見ながら選べる。
  //     PC は 2 カラムで画像が常に見えるため従来位置（価格の下）のまま。
  //     移動先の media-gallery は product-info の内側なので、Dawn の
  //     variant 切替再レンダリング（this.querySelector で置換）も壊れない。
  // -------------------------------------------------------------------------
  const vpSpQuery = window.matchMedia('(max-width: 749px)');
  function relocateVariantPicker() {
    const vs = document.querySelector('variant-selects, variant-radios');
    if (!vs || !vs.parentNode) return;
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"], .thumbnail-slider');
    const entry = document.querySelector('.ns-sizecheck-entry');

    if (vpSpQuery.matches) {
      let home = document.querySelector('.ns-vp-home');
      if (!home) {
        home = document.createElement('div');
        home.className = 'ns-vp-home';
        home.hidden = true;
        vs.parentNode.insertBefore(home, vs);
      }
      if (!gallery.contains(vs)) {
        vs.classList.add('ns-vp-in-gallery');
        gallery.insertBefore(vs, thumbs || null);
      }
      if (entry && !gallery.contains(entry)) {
        entry.classList.add('ns-vp-in-gallery');
        vs.after(entry);
      }
    } else {
      const home = document.querySelector('.ns-vp-home');
      if (home && gallery.contains(vs)) {
        vs.classList.remove('ns-vp-in-gallery');
        home.after(vs);
      }
      if (entry && gallery.contains(entry)) {
        entry.classList.remove('ns-vp-in-gallery');
        document.querySelector('variant-selects, variant-radios')?.after(entry);
      }
    }
  }
  function setupVariantPickerRelocation(data) {
    const onChange = () => { relocateVariantPicker(); ensureVariantMirror(data); };
    try { vpSpQuery.addEventListener('change', onChange); }
    catch (_) { vpSpQuery.addListener(onChange); }
  }

  // -------------------------------------------------------------------------
  // 8.8 SP: 価格の下（数量の上）にバリアント選択の第 2 UI（ミラー）を表示
  //     本体（§8.7 で画像直下へ移動）と従来位置のどちらからでも選べるようにする。
  //     Dawn の variant-selects は 1 個前提で、DOM を複製すると id と radio の
  //     name グループが衝突するため複製はしない。実 radio の状態を読み取って
  //     描画する軽量ミラーを自前で持ち、クリックは実 radio の click() へ橋渡し。
  //     Dawn の再レンダリングは watchVariantChanges(reapply) が拾ってミラーを
  //     再構築する＝どちらを操作しても常に同期する。
  // -------------------------------------------------------------------------
  function ensureVariantMirror(data) {
    const existing = document.querySelector('.ns-vp-mirror');
    if (!vpSpQuery.matches) { existing?.remove(); return; }
    const vs = document.querySelector('variant-selects, variant-radios');
    const home = document.querySelector('.ns-vp-home');
    if (!vs || !home) { existing?.remove(); return; }

    const swatches = data?.metafields?.color_swatches?.swatches || [];
    const swatchByEn = new Map(swatches.map(s => [s.name_en, s]));

    const groups = [];
    vs.querySelectorAll('fieldset').forEach(fs => {
      const legend = fs.querySelector('legend');
      const label = (legend?.childNodes[0]?.textContent || legend?.textContent || '').trim();
      const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
      if (inputs.length >= 2) groups.push({ label, inputs, fs });
    });
    if (!groups.length) { existing?.remove(); return; }

    // 選択状態と実 radio が変わっていなければ再構築しない
    // （開いているプルダウンを閉じない・buildColorPicker の document リスナ蓄積防止）
    const sig = groups.map(g =>
      `${g.label}:${g.inputs.find(i => i.checked)?.value || ''}:${g.inputs.length}`).join('|');
    if (existing && existing.dataset.nsSig === sig && existing.__nsAnchor?.isConnected) return;

    const mirror = existing || document.createElement('div');
    mirror.className = 'ns-vp-mirror';
    mirror.dataset.nsSig = sig;
    mirror.__nsAnchor = groups[0].inputs[0];
    mirror.textContent = '';
    groups.forEach(g => {
      const row = document.createElement('div');
      row.className = 'ns-vp-mirror__group';
      const title = document.createElement('span');
      title.className = 'ns-vp-mirror__label';
      title.textContent = g.label;
      row.appendChild(title);

      // 色は本体（画像直下）と同一のプルダウン UI を複製生成して統一する。
      // buildColorPicker のクリックは実 radio への橋渡しなので同期もそのまま成立
      const isColor = /色|color|colour/i.test(g.label);
      if (isColor && swatchByEn.size > 0) {
        const picker = buildColorPicker(g.fs, swatchByEn);
        if (picker) {
          row.appendChild(picker);
          mirror.appendChild(row);
          return;
        }
      }

      const items = document.createElement('div');
      const isSize = g.label.includes('サイズ');
      items.className = 'ns-vp-mirror__items' + (isSize ? ' ns-vp-mirror__items--fit' : '');
      g.inputs.forEach(input => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ns-vp-mirror__pill';
        if (input.checked) b.classList.add('is-on');
        if (input.disabled) b.classList.add('is-off');
        b.textContent = input.value;
        b.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
        b.addEventListener('click', () => {
          if (input.checked) return;
          input.click();
        });
        items.appendChild(b);
      });
      row.appendChild(items);
      mirror.appendChild(row);
    });
    if (!mirror.parentNode) home.after(mirror);
  }

  // -------------------------------------------------------------------------
  // 8.9 サイズ pill の 1 行等分グリッド化用ラッパー
  //     fieldset 直下の input+label をひとつの .ns-size-row に包む。
  //     fieldset 自体を flex/grid にしない理由：legend の特殊レイアウトにより
  //     エンジン実装差が大きい既知領域のため（WebKit 旧実装で不成立）。
  //     Dawn の再レンダリングで消えるため reapply から毎回呼ばれる（冪等）。
  // -------------------------------------------------------------------------
  function enhanceSizePills() {
    document.querySelectorAll('variant-selects fieldset, variant-radios fieldset').forEach(fs => {
      const legend = fs.querySelector('legend');
      if (!legend || !legend.textContent.includes('サイズ')) return;
      const firstRadio = fs.querySelector('input[type="radio"]');
      if (!firstRadio) return;
      const host = firstRadio.parentElement;
      if (!host || host.classList.contains('ns-size-row')) return;
      const nodes = Array.from(host.children).filter(el =>
        el.matches?.('input[type="radio"], label'));
      if (nodes.length < 2) return;
      const row = document.createElement('div');
      row.className = 'ns-size-row';
      host.insertBefore(row, nodes[0]);
      nodes.forEach(n => row.appendChild(n));
    });
  }

  // -------------------------------------------------------------------------
  // 9. variant 切替監視
  // -------------------------------------------------------------------------
  function watchVariantChanges(reapply) {
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      try {
        if (window.PUB_SUB_EVENTS.variantChange) {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
            requestAnimationFrame(() => requestAnimationFrame(reapply));
            setTimeout(reapply, 100);
            setTimeout(reapply, 500);
          });
        }
        if (window.PUB_SUB_EVENTS.optionValueSelectionChange) {
          window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, () =>
            requestAnimationFrame(() => requestAnimationFrame(reapply)));
        }
      } catch (_) {}
    }

    const root = document.querySelector('product-info, main, body');
    if (!root) return;

    let pending = false;
    const obs = new MutationObserver((mutations) => {
      let touched = false;
      for (const m of mutations) {
        if (m.type !== 'childList') continue;
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches?.('variant-selects, variant-radios') ||
            node.querySelector?.('variant-selects, variant-radios') ||
            node.matches?.('.product-form__input--swatch, .product-form__input--pill, .product-form__input, legend, .form__label') ||
            node.querySelector?.('.product-form__input--swatch, .product-form__input--pill, .product-form__input')
          ) { touched = true; break; }
        }
        if (touched) break;
      }
      if (touched && !pending) {
        pending = true;
        requestAnimationFrame(() => { pending = false; reapply(); });
      }
    });
    obs.observe(root, { childList: true, subtree: true });

    document.addEventListener('change', e => {
      if (e.target?.matches('input[type="radio"], select')) {
        setTimeout(reapply, 100);
        setTimeout(reapply, 400);
        setTimeout(reapply, 1200);
      }
    }, true);
  }

  // -------------------------------------------------------------------------
  // 10. 起動
  // -------------------------------------------------------------------------
  function init() {
    const data = loadProductData();
    if (!data) return;

    customizeUnavailableLabel();

    function reapply() {
      try { pairTitleWithPrice();          } catch (e) { console.warn('[ns] title',  e); }
      try { applyColorPicker(data);        } catch (e) { console.warn('[ns] picker', e); }
      try { ensureDeliveryInfo(data);      } catch (e) { console.warn('[ns] ship',   e); }
      try { setupSizeGuide(data);          } catch (e) { console.warn('[ns] size',   e); }
      try { relocateVariantPicker();       } catch (e) { console.warn('[ns] vp-move', e); }
      try { enhanceSizePills();            } catch (e) { console.warn('[ns] pills',  e); }
      try { ensureVariantMirror(data);     } catch (e) { console.warn('[ns] vp-mirror', e); }
      try { ensureSizeCheckEntry(data);    } catch (e) { console.warn('[ns] sizecheck', e); }
      try { patchUnavailableButtonText();  } catch (e) { console.warn('[ns] label',  e); }
    }

    reapply();
    setupInstantImageSwap(data);
    setupStickyCart(data);
    setupDeliveryGuide(data);
    setupSizeCheck(data);
    disableDawnVariantMediaSwap();
    setupVariantPickerRelocation(data);
    watchVariantChanges(reapply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
